import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiGet, apiPost } from "./EmailApi";
import "./InboxSecuritySection.css";

const defaultForm = {
  businessEmailId: "",
  spamFilterEnabled: true,
  antivirusEnabled: true,
  encryptionEnabled: false,
  twoFactorEnabled: false,
  allowedIps: "",
  blockedDomains: "",
};

function InboxSecuritySection() {
  const [emails, setEmails] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const selectedEmailExists = useMemo(() => {
    if (!form.businessEmailId) return false;
    return emails.some((item) => String(item.id) === String(form.businessEmailId));
  }, [emails, form.businessEmailId]);

  const updateForm = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const [emailsRes, policiesRes] = await Promise.all([
        apiGet("/api/email/my-emails"),
        apiGet("/api/email/security"),
      ]);

      if (emailsRes?.success === false) {
        throw new Error(emailsRes?.message || "Failed to load business emails.");
      }

      if (policiesRes?.success === false) {
        throw new Error(policiesRes?.message || "Failed to load security policies.");
      }

      setEmails(Array.isArray(emailsRes?.data) ? emailsRes.data : []);
      setPolicies(Array.isArray(policiesRes?.data) ? policiesRes.data : []);
    } catch (err) {
      setEmails([]);
      setPolicies([]);
      setError(err.message || "Security fetch failed.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const intervalId = window.setInterval(fetchData, 10000);
    return () => window.clearInterval(intervalId);
  }, [fetchData]);

  const handleSave = async (event) => {
    event.preventDefault();

    setMessage("");
    setError("");

    if (!form.businessEmailId || !selectedEmailExists) {
      setError("Please select a valid business email.");
      return;
    }

    try {
      setSaving(true);

      const data = await apiPost("/api/email/security", form);

      if (!data?.success) {
        throw new Error(data?.message || "Failed to save security policy.");
      }

      setMessage("Security policy saved successfully.");
      setForm(defaultForm);
      fetchData();
    } catch (err) {
      setError(err.message || "Failed to save security policy.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="email-panel-card is-section">

      {/* HEADER */}
      <div className="is-header">
        <div>
          <span className="is-badge">Email Protection</span>
          <h2>Inbox Security</h2>
          <p>Secure your email inbox with advanced protection settings.</p>
        </div>

        <div className="is-icon">🔒</div>
      </div>

      {error && <div className="is-alert error">{error}</div>}
      {message && <div className="is-alert success">{message}</div>}

      {/* FORM */}
      <form className="email-panel-form is-form" onSubmit={handleSave}>
        <select
          value={form.businessEmailId}
          onChange={(e) => updateForm("businessEmailId", e.target.value)}
          required
        >
          <option value="">Select Business Email</option>
          {emails.map((item) => (
            <option key={item.id} value={item.id}>
              {item.email_address}
            </option>
          ))}
        </select>

        <label><input type="checkbox" checked={form.spamFilterEnabled} onChange={(e)=>updateForm("spamFilterEnabled", e.target.checked)} /> Spam Filter</label>
        <label><input type="checkbox" checked={form.antivirusEnabled} onChange={(e)=>updateForm("antivirusEnabled", e.target.checked)} /> Antivirus</label>
        <label><input type="checkbox" checked={form.encryptionEnabled} onChange={(e)=>updateForm("encryptionEnabled", e.target.checked)} /> Encryption</label>
        <label><input type="checkbox" checked={form.twoFactorEnabled} onChange={(e)=>updateForm("twoFactorEnabled", e.target.checked)} /> 2FA</label>

        <input type="text" placeholder="Allowed IPs" value={form.allowedIps} onChange={(e)=>updateForm("allowedIps", e.target.value)} />
        <input type="text" placeholder="Blocked Domains" value={form.blockedDomains} onChange={(e)=>updateForm("blockedDomains", e.target.value)} />

        <button type="submit">{saving ? "Saving..." : "Save Policy"}</button>
      </form>

      {/* LIST */}
      <div className="email-panel-list is-list">
        {loading ? (
          <p>Loading...</p>
        ) : policies.length === 0 ? (
          <p>No policies found</p>
        ) : (
          policies.map((item) => (
            <div className="email-panel-item is-item" key={item.id}>
              <h4>{item.email_address}</h4>
              <p>Spam: {item.spam_filter_enabled ? "Enabled" : "Disabled"}</p>
              <p>Antivirus: {item.antivirus_enabled ? "Enabled" : "Disabled"}</p>
              <p>Encryption: {item.encryption_enabled ? "Enabled" : "Disabled"}</p>
              <p>2FA: {item.two_factor_enabled ? "Enabled" : "Disabled"}</p>
              <p>IPs: {item.allowed_ips || "-"}</p>
              <p>Blocked: {item.blocked_domains || "-"}</p>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default InboxSecuritySection;